import React from 'react';
import { PAYMENT_TYPE } from '../utils/constants';
import { ItemToSchedule } from '../contexts/ModuleContext';
import { EligibilityPlanProps } from '../contexts/PaymentContext';
import { ProductItemOriginal, ProductItemProps, Rule } from '../models/Product';
import { Payment, Resource, ScheduleItemProps } from '../models/Schedule';
import { ScheduleSpecialist, ScheduleSpecialistBuilder } from '../models/ScheduleSpecialist';
import { verifyEligibility } from '../modules/eligibility';
import { PAGES } from '../Router/pages';
import { gtmEvent } from '../utils/gtm';
import { useGenericModal } from './useGenericModal';
import { useValidateProductRules, ValidateProductData } from './useValidateProductRules';
import { TYPE_ROUTE_PRODUCT, useProductRuleActions, useProductRuleActionsProps } from './useProductRuleActions';
import { useSchedulingMount, useSchedulingMountProps } from './useSchedulingMount';
import { Specialist } from '../models/Specialist';
import { PatientProps } from '../contexts/PatientContext';

export type genericRuleCatalog = {
  rule: string;
  action: string;
  value: string;
};

export type AddPatientProps = {
  productSelected?: ProductItemOriginal;
  patient: PatientProps;
  itemToSchedule: ItemToSchedule;
  schedule?: ScheduleItemProps;
  validaeRules?: boolean;
  specialist?: Specialist;
};

export type usePatientModuleProps = {
  changeProduct: (product: ProductItemProps) => void;
  setEligibilityPlans: (plans: EligibilityPlanProps[]) => void;
  eligibilityPlans: EligibilityPlanProps[];
  goTo: (route: number, state?: any) => void;
  addPatientList: (patient: PatientProps) => PatientProps;
  changeScheduleSpecialist: (schedule: ScheduleSpecialist) => void;
  scheduleSpecialistSelected: ScheduleSpecialist;
  setScheduleSpecialistSelected: (value: ScheduleSpecialist) => void;
} & useSchedulingMountProps &
  useProductRuleActionsProps;

export const usePatientModule = ({
  addPatientList,
  changeProduct,
  eligibilityPlans,
  setEligibilityPlans,
  goTo,
  changeSchedule,
  selectedSchedule,
  openModal,
  scheduleSpecialistSelected,
  setScheduleSpecialistSelected,
  changeScheduleSpecialist
}: usePatientModuleProps) => {
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalNoEligibility, setIsModalNoEligibility] = React.useState(false);
  const [isModalProductNotCoveredVisible, setIsModalProductNotCoveredVisible] = React.useState(false);

  const useScheduling = useSchedulingMount({ changeSchedule, selectedSchedule });
  const validateRules = useValidateProductRules();
  const genericModal = useGenericModal({
    goTo,
    schedule: useScheduling.schedule
  });
  const productRuleActions = useProductRuleActions({ goTo, openModal, schedule: useScheduling.schedule });

  function changeScheduleResource(resource: Resource | null) {
    useScheduling.addResource(!!resource, resource || undefined);
    useScheduling.updateSchedule();
  }

  async function verifyRules({
    patient,
    product,
    schedule
  }: {
    patient: PatientProps;
    product: ProductItemOriginal;
    schedule: ScheduleItemProps;
  }) {
    const resultValidate = await validateRules({
      patient,
      product,
      schedule
    });
    if (!resultValidate.error) return resultValidate.data;
    if (resultValidate.error) setIsModalSystemUnavailableVisible(true);
  }

  function checkPaymentHasSchedule(schedule: ScheduleItemProps) {
    return schedule.payment || undefined;
  }

  async function validateElegibilityPlans({
    schedule,
    patient
  }: {
    schedule: ScheduleItemProps;
    patient: PatientProps;
  }): Promise<Payment | undefined> {
    let eligibilities = eligibilityPlans;
    const currentPaymentSchedule = checkPaymentHasSchedule(schedule);
    if (!currentPaymentSchedule) {
      const { data, error } = await verifyEligibility({
        eligibilityType: 'PRODUCT',
        document: patient.document,
        documentType: patient.documentType,
        itemId: schedule.productId,
        birthDate: patient.birthDate
      });

      if (error) setIsModalNoEligibility(true);
      if (!error && data.length) eligibilities = data;
    }
    if (currentPaymentSchedule) return currentPaymentSchedule;
    if (eligibilities.length === 0) setIsModalNoEligibility(true);
    if (eligibilities.length > 1) {
      setEligibilityPlans(eligibilities);
      goTo(PAGES.PAYMENT_VIEW.id);
    }
    if (eligibilities.length === 1) {
      const [plan] = eligibilities;
      return {
        type: PAYMENT_TYPE.B2B,
        eligibility: {
          idInsurance: plan.id_convenio,
          idCustomer: plan.id_cliente,
          codInsurance: plan.cod_convenio,
          codPlan: plan.cod_plano,
          codOffice: plan.cod_escritorio,
          plan: plan.nome,
          image: plan.image
        }
      } as Payment;
    }
  }

  async function validateAndSaveSchedule({
    patient,
    product
  }: {
    patient: PatientProps;
    product: ProductItemOriginal;
  }) {
    let validatedRules: null | ValidateProductData = null;
    const result = await verifyRules({
      patient,
      product,
      schedule: useScheduling.schedule
    });
    if (result) validatedRules = result;

    if (validatedRules) {
      changeProduct(validatedRules.product);
      const genericModals = genericModal.addGenericModals({
        productRules: validatedRules.product.rules,
        additional: validatedRules.modals
      });
      //
      changeScheduleResource(validatedRules.resource);
      return {
        product: validatedRules.product,
        resource: validatedRules.resource,
        genericModals
      };
    }
  }

  function makeSpecialistSchedule(props: { patientId: string; specialist: Specialist | undefined }) {
    const scheduleSpecialist = ScheduleSpecialistBuilder();
    scheduleSpecialist.setPatient(props.patientId);
    changeScheduleSpecialist(scheduleSpecialist.build());
  }

  async function saveAndRouteSchedule(props: { patient: PatientProps; product: ProductItemOriginal }) {
    const validatedSchedule = await validateAndSaveSchedule(props);

    if (validatedSchedule)
      return productRuleActions.routeScheduleByProduct(
        validatedSchedule.product,
        validatedSchedule.resource,
        validatedSchedule.genericModals
      );
  }

  const handleAddPatient = async ({
    productSelected,
    patient,
    itemToSchedule,
    specialist,
    schedule
  }: AddPatientProps) => {
    const patientCreated = addPatientList(patient);
    if (itemToSchedule === 'SPECIALIST' && patientCreated.idPatient) {
      makeSpecialistSchedule({
        specialist,
        patientId: patientCreated.idPatient
      });
      goTo(PAGES.SERVICE_TYPE_PAGE.id);
      return { type: TYPE_ROUTE_PRODUCT.ok };
    }

    if (itemToSchedule === 'PRODUCT' || itemToSchedule === 'PRODUCT_ENV')
      if (useScheduling.schedule && productSelected) {
        useScheduling.addPatient(patientCreated.idPatient || '');
        useScheduling.addPatientFull(patientCreated);

        if (productSelected.is_b2b) {
          const eligibility = await validateElegibilityPlans({
            patient: patientCreated,
            schedule: schedule || useScheduling.schedule
          });

          if (eligibility) useScheduling.addPayment(eligibility);
          if (schedule?.productId) useScheduling.addProduct(productSelected);

          if (productSelected.is_b2b && !eligibility) return undefined;
          else if (useScheduling.schedule.payment) {
            return await saveAndRouteSchedule({
              patient: patientCreated,
              product: productSelected
            });
          }
        }

        if (!productSelected.is_b2b) {
          if (!useScheduling.schedule.payment) {
            useScheduling.addPayment({
              type: PAYMENT_TYPE.PARTICULAR
            });
          }
          return await saveAndRouteSchedule({
            patient: patientCreated,
            product: productSelected
          });
        }
      }
  };

  const createSchedule = async (props: {
    product: ProductItemProps;
    patient: PatientProps;
    itemToSchedule: ItemToSchedule;
    payment?: Payment;
  }) => {
    useScheduling.addProduct(props.product);

    if (props.payment) useScheduling.addPayment(props.payment);
    else useScheduling.resetPayment();

    return await handleAddPatient({
      patient: props.patient,
      productSelected: props.product,
      itemToSchedule: props.itemToSchedule
    });
  };

  return {
    handleAddPatient,
    isModalSystemUnavailableVisible,
    isModalNoEligibility,
    isModalProductNotCoveredVisible,
    setIsModalProductNotCoveredVisible,
    setIsModalSystemUnavailableVisible,
    setIsModalNoEligibility,
    genericModal,
    createSchedule,
    useScheduling
  };
};
